import { logger } from '@adornis/base/logging.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { Counter } from '@adornis/counter/counter.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { StudentType } from '@chancenwerk/enums/enums.js';
import { Observable } from 'rxjs';
import { ChancenPermission } from '../../../permissions.js';
import { BUT_SCHOOL_YEAR_PREFIX, currentSchoolYearLabel, previousSchoolYearLabel } from '../../../school-year.js';
import type { ChancenwerkUser } from '../entities/ChancenwerkUser.js';
import { Student } from '../entities/Student.js';
import type { AnyBulkWriteOperation, Filter } from 'mongodb';

const BATCH_SIZE = 500;

/**
 * Marks a progress emission from {@link migrateButToCurrentSchoolYear} as carrying an error message instead
 * of a status update. `waitFor`'s subscribe handler in the dialog engine has no `error` channel -- it only
 * resolves on `complete` -- so a thrown exception would otherwise leave the client's loading dialog stuck
 * forever. Emitting the error as a distinguishable final string keeps the observable always completing, and
 * lets the client tell success from failure without the engine needing a richer contract.
 */
export const MIGRATION_ERROR_PREFIX = '__ERROR__';

function counterKeyForYear(yearLabel: string): string {
  return `but-year-migration:${yearLabel}`;
}

// Same shape as Student.active (Student.ts), expressed as a Mongo filter: no kuendigung at all, or one whose
// austrittsdatum hasn't passed yet.
function activeUSStudentFilter(): Filter<PartialEntityData<Student>> {
  return {
    _class: Student._class,
    schuelertyp: StudentType.US,
    $or: [
      { 'kuendigung.austrittsdatum': { $exists: false } },
      { 'kuendigung.austrittsdatum': null },
      { 'kuendigung.austrittsdatum': { $gt: new Date() } },
    ],
  };
}

/**
 * Bulk-creates the current school year's BuT entry for every active student who was BuT-entitled in the
 * immediately preceding school year, carrying the entitlement forward (see {@link BuT.copyToSchoolYear}).
 * Runs once per school year -- the {@link Counter} guard makes a repeat click (or a second deploy) a no-op,
 * so case workers correct exceptions on individual students afterwards rather than re-running this.
 *
 * Replaces the old, hardcoded-per-year `butMigration.ts` startup migration: this derives the from/to years
 * from {@link currentSchoolYearLabel}/{@link previousSchoolYearLabel}, so it needs no edits next summer, and
 * it's a permission-gated action a case worker triggers explicitly instead of something that runs silently
 * on server startup.
 *
 * Reads the raw `students` collection directly rather than going through `UserAPI.subscribeByFilter`: that
 * subscription is served off a materialized view that is only eventually consistent with the underlying
 * collection (rebuilt asynchronously off a worker queue), which is the wrong trade-off for a bulk job that
 * has to see every currently-eligible student, not a UI-friendly near-current snapshot.
 *
 * `onProgress`, if given, is called after every batch with (students migrated so far, total students that
 * will be migrated) so a caller can report live progress. Exported separately from the
 * {@link migrateButToCurrentSchoolYear} subscription so tests can call it without having to fake an
 * authenticated, permissioned request context.
 */
export async function runButYearMigration(onProgress?: (done: number, total: number) => void): Promise<number> {
  const yearLabel = currentSchoolYearLabel(BUT_SCHOOL_YEAR_PREFIX);
  const previousYearLabel = previousSchoolYearLabel(BUT_SCHOOL_YEAR_PREFIX);
  const counterKey = counterKeyForYear(yearLabel);

  if ((await Counter.getCurrentCounter(counterKey)()) > 0) {
    logger.info(`[migrateButToCurrentSchoolYear] ${yearLabel} was already migrated, skipping`);
    return 0;
  }

  const studentCol = await getRawCollection<PartialEntityData<Student>>(Student._collectionName);
  const filter = activeUSStudentFilter();

  const totalToMigrate = await studentCol.countDocuments({
    $and: [
      filter,
      { buts: { $elemMatch: { year: previousYearLabel, BuTBerechtigung: true } } },
      { buts: { $not: { $elemMatch: { year: yearLabel } } } },
    ],
  });

  let updated = 0;

  for (let i = 0; ; i++) {
    const rawStudents = await studentCol
      .find(filter, { projection: { _id: 1, buts: 1 } })
      .sort({ _id: 1 })
      .skip(i * BATCH_SIZE)
      .limit(BATCH_SIZE)
      .toArray();
    const students = rawStudents.map(raw => new Student(raw));

    const writes: Array<AnyBulkWriteOperation<PartialEntityData<Student>>> = [];
    for (const student of students) {
      const previous = student.buts?.find(but => but.year === previousYearLabel);
      if (!previous?.BuTBerechtigung) continue;

      const alreadyMigrated = student.buts?.some(but => but.year === yearLabel);
      if (alreadyMigrated) continue;

      student.buts.push(previous.copyToSchoolYear(yearLabel));
      updated++;

      writes.push({
        updateOne: {
          filter: { _id: student._id },
          update: { $set: { buts: student.buts.map(but => but.toJSON()) } },
        },
      });
    }

    if (writes.length > 0) {
      await studentCol.bulkWrite(writes);
    }

    logger.info(`[migrateButToCurrentSchoolYear] Migrated batch ${i} (${writes.length} writes)`);
    onProgress?.(updated, totalToMigrate);

    if (rawStudents.length < BATCH_SIZE) {
      break;
    }
  }

  await Counter.getIncrementingNumber(counterKey)();
  logger.info(`[migrateButToCurrentSchoolYear] Done, updated ${updated} students for ${yearLabel}`);
  return updated;
}

/**
 * A subscription rather than a mutation so the client can show live progress via `ChromiumDialog.waitFor`,
 * which understands an `Observable<string>`: every emission replaces the dialog's text, and the promise it
 * returns resolves with the last one. See {@link MIGRATION_ERROR_PREFIX} for how failures are represented
 * within that single string channel.
 */
export const migrateButToCurrentSchoolYear = registerSubscription({
  type: () => String,
  operationName: 'migrateButToCurrentSchoolYear',
  params: [],
  resolve: () => () => {
    return new Observable<string>(subscriber => {
      void (async () => {
        try {
          const caller = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
            roles: { name: 1, permissions: 1, contexts: 1 },
          });
          if (!caller?.hasPermission(ChancenPermission.raum.MIGRATE_BUT_YEAR)) {
            throw new Error('Keine Berechtigung, das BuT-Jahr zu aktualisieren.');
          }

          const updated = await runButYearMigration((done, total) => {
            subscriber.next(`${done}/${total} Schüler:innen übertragen…`);
          });

          subscriber.next(`${updated} Schüler:innen übertragen.`);
          subscriber.complete();
        } catch (e) {
          subscriber.next(`${MIGRATION_ERROR_PREFIX}${e instanceof Error ? e.message : 'Unbekannter Fehler'}`);
          subscriber.complete();
        }
      })();
    });
  },
});

/** Whether {@link migrateButToCurrentSchoolYear} has already run for the currently running school year. */
export const hasMigratedButForCurrentSchoolYear = registerQuery({
  type: () => Boolean,
  operationName: 'hasMigratedButForCurrentSchoolYear',
  params: [],
  resolve: () => async () => {
    const yearLabel = currentSchoolYearLabel(BUT_SCHOOL_YEAR_PREFIX);
    return (await Counter.getCurrentCounter(counterKeyForYear(yearLabel))()) > 0;
  },
});
